// frontend/src/pages/SchoolProjects/TechnicianParts/TechnicianParts.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Tab,
  Tabs,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';

const TechnicianParts = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState({
    active: 0,
    overdue: 0,
    returned: 0,
    totalIssued: 0,
    technicians: 0,
    parts: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadStats = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get assignments summary
      const assignments = await api.getTechnicianAssignments();
      const techs = await api.getTechniciansList();
      const parts = await api.getComputerParts();
      
      const active = assignments.data?.filter(a => a.status === 'issued').length || 0;
      const overdue = assignments.data?.filter(a => a.status === 'overdue').length || 0;
      const returned = assignments.data?.filter(a => a.status === 'returned').length || 0;
      const totalIssued = assignments.data?.reduce((sum, a) => sum + a.quantity, 0) || 0;
      
      setStats({
        active,
        overdue,
        returned,
        totalIssued,
        technicians: techs.data?.length || 0,
        parts: parts.data?.length || 0
      });
    } catch (err) {
      console.error('Error loading stats:', err);
      setError('Failed to load statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    switch(newValue) {
      case 0:
        navigate('/school-projects/technician-parts/issue');
        break;
      case 1:
        navigate('/school-projects/technician-parts/assignments');
        break;
      case 2:
        navigate('/school-projects/technician-parts/return');
        break;
      default:
        break;
    }
  };

  const StatCard = ({ title, value, icon, color, subtext }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div">
              {value}
            </Typography>
            {subtext && (
              <Typography variant="caption" color="textSecondary">
                {subtext}
              </Typography>
            )}
          </Box>
          <Box sx={{ color: `${color}.main` }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Technician Parts Management
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage computer parts assignments to technicians
          </Typography>
        </Box>
        <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={loadStats}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Active Assignments"
            value={stats.active}
            icon={<AssignmentIcon sx={{ fontSize: 40 }} />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Overdue"
            value={stats.overdue}
            icon={<AssignmentIcon sx={{ fontSize: 40, color: 'error.main' }} />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Returned"
            value={stats.returned}
            icon={<InventoryIcon sx={{ fontSize: 40, color: 'success.main' }} />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Total Parts Issued"
            value={stats.totalIssued}
            icon={<InventoryIcon sx={{ fontSize: 40 }} />}
            color="info"
            subtext="parts"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Technicians"
            value={stats.technicians}
            icon={<PersonIcon sx={{ fontSize: 40 }} />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Available Parts"
            value={stats.parts}
            icon={<SchoolIcon sx={{ fontSize: 40 }} />}
            color="secondary"
          />
        </Grid>
      </Grid>

      {/* Navigation Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              py: 2
            }
          }}
        >
          <Tab 
            icon={<AddIcon />} 
            iconPosition="start" 
            label="Issue Parts" 
          />
          <Tab 
            icon={<AssignmentIcon />} 
            iconPosition="start" 
            label="View Assignments" 
          />
          <Tab 
            icon={<InventoryIcon />} 
            iconPosition="start" 
            label="Return Parts" 
          />
          <Tab 
            icon={<HistoryIcon />} 
            iconPosition="start" 
            label="History" 
          />
        </Tabs>
      </Paper>

      {/* Quick Actions */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Quick Actions
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/school-projects/technician-parts/issue')}
              size="large"
            >
              Issue New Parts
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<InventoryIcon />}
              onClick={() => navigate('/school-projects/technician-parts/return')}
              size="large"
            >
              Return Parts
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AssignmentIcon />}
              onClick={() => navigate('/school-projects/technician-parts/assignments')}
              size="large"
            >
              View All
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<PersonIcon />}
              onClick={() => navigate('/school-projects/technicians')}
              size="large"
            >
              Manage Technicians
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Recent Assignments Preview */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Recent Activity
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" color="textSecondary" align="center">
          Select a tab above to view detailed information
        </Typography>
      </Paper>
    </Box>
  );
};

export default TechnicianParts;